import { useState, type FormEvent } from 'react';
import type { AttachedSkillEvidenceResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { EVIDENCE_TYPE_OPTIONS, evidenceTypeLabel, formatCalendarDate } from './attributeDisplay';
import styles from './AssessmentForm.module.css';

/**
 * Attaching evidence to an existing Skill record (docs/DOMAIN_DECISIONS.md,
 * "Skill Evidence"). Unlike an attribute assessment, there is no recording
 * action to attach evidence alongside — `employee_skill` has no write path
 * at all — so this is a standalone attachment onto a record that already
 * exists, offered as a per-skill action rather than folded into a bigger
 * form. One item per submission, deliberately: the AssessmentForm's
 * multi-item composer exists because it is bundled with a rating that is
 * itself one event; attaching evidence here is already the whole event.
 */
export function SkillEvidenceForm({
  employeeId,
  skillId,
  skillName,
  onAttached,
  onClose,
}: {
  employeeId: string;
  skillId: string;
  skillName: string;
  onAttached: () => void;
  onClose: () => void;
}) {
  const [evidenceTypeCode, setEvidenceTypeCode] = useState('');
  const [description, setDescription] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [restricted, setRestricted] = useState(false);
  const [state, setState] = useState<
    { status: 'editing' } | { status: 'submitting' } | { status: 'attached'; result: AttachedSkillEvidenceResponse } | { status: 'failed'; message: string }
  >({ status: 'editing' });

  const submitting = state.status === 'submitting';
  const isValid = evidenceTypeCode !== '' && description.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !isValid) return;

    setState({ status: 'submitting' });
    try {
      const result = await apiPost<AttachedSkillEvidenceResponse>(`/employees/${employeeId}/skills/${skillId}/evidence`, {
        evidence: [
          {
            evidenceTypeCode,
            description: description.trim(),
            occurredOn: occurredOn || undefined,
            externalReference: externalReference.trim() || undefined,
            visibility: restricted ? 'restricted' : undefined,
          },
        ],
      });
      setState({ status: 'attached', result });
      onAttached();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (state.status === 'attached') {
    return (
      <div className={styles.form} role="status">
        <p className={styles.recorded}>Evidence attached to {skillName}.</p>
        <ul className={styles.evidenceSummary}>
          {state.result.evidence.map((item) => (
            <li key={item.id}>
              <strong>{evidenceTypeLabel(item.evidenceTypeCode)}</strong>
              {item.visibility === 'restricted' ? <span className={styles.restrictedBadge}>Restricted</span> : null}
              : {item.description}
              {item.occurredOn ? ` (${formatCalendarDate(item.occurredOn)})` : ''}
            </li>
          ))}
        </ul>
        <button type="button" className={styles.secondary} onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} aria-label={`Attach evidence to ${skillName}`}>
      <div className={styles.field}>
        <label htmlFor={`skill-evidence-type-${skillId}`} className={styles.label}>
          Type
        </label>
        <select
          id={`skill-evidence-type-${skillId}`}
          className={styles.evidenceSelect}
          value={evidenceTypeCode}
          onChange={(event) => setEvidenceTypeCode(event.target.value)}
          disabled={submitting}
          required
        >
          <option value="">Choose a type…</option>
          {EVIDENCE_TYPE_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label htmlFor={`skill-evidence-description-${skillId}`} className={styles.label}>
          Description
        </label>
        <textarea
          id={`skill-evidence-description-${skillId}`}
          className={styles.textarea}
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={submitting}
          placeholder="What this evidence is"
          maxLength={2000}
          required
        />
      </div>

      <div className={styles.evidenceRow}>
        <label className={styles.evidenceFieldLabel} htmlFor={`skill-evidence-date-${skillId}`}>
          When <span className={styles.optional}>optional</span>
        </label>
        <input
          id={`skill-evidence-date-${skillId}`}
          className={styles.text}
          type="date"
          value={occurredOn}
          onChange={(event) => setOccurredOn(event.target.value)}
          disabled={submitting}
        />
        <label className={styles.evidenceFieldLabel} htmlFor={`skill-evidence-reference-${skillId}`}>
          Reference <span className={styles.optional}>optional</span>
        </label>
        <input
          id={`skill-evidence-reference-${skillId}`}
          className={styles.text}
          type="text"
          value={externalReference}
          onChange={(event) => setExternalReference(event.target.value)}
          disabled={submitting}
          placeholder="A pointer to it — a document, a link"
          maxLength={500}
        />
      </div>

      <label className={styles.restrictedOption}>
        <input
          type="checkbox"
          checked={restricted}
          onChange={(event) => setRestricted(event.target.checked)}
          disabled={submitting}
        />
        Restrict this item
      </label>
      <p className={styles.help}>
        Afterwards visible only to the employee this skill belongs to, and to admin/HR — not to anyone else who can
        otherwise see this record, including you, unless you are one of those.
      </p>

      {state.status === 'failed' ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button type="submit" className={styles.primary} disabled={submitting || !isValid}>
          {submitting ? 'Attaching…' : 'Attach evidence'}
        </button>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
